/**
 * Cases Domain Seed Data
 *
 * Contains all seed data for Case Management Service (29):
 * - Cases (support tickets/loan inquiries)
 * - Case Messages
 *
 * Cases are linked to consumers and represent support tickets,
 * document requests, payment issues, and general inquiries.
 */

import { SEED_COLORS } from '../../theme/seed-colors';
import { seedId } from '../utils';
import { CONSUMER_IDS } from './consumers';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// CASE IDs
// =============================================================================

export const CASE_IDS = {
  // OpsMatrix Capital - VIP Tier
  MARCUS_INSURANCE_CERT: seedId('case-marcus-insurance', 1),
  MARCUS_PORTFOLIO_EXPANSION: seedId('case-marcus-portfolio', 2),
  ROBERT_TAX_UPLOAD: seedId('case-robert-tax', 1),
  ROBERT_WIRE_CONFIRM: seedId('case-robert-wire', 2),
  ROBERT_PAYOFF_REQUEST: seedId('case-robert-payoff', 3),

  // OpsMatrix Capital - Trusted Tier
  ALICIA_DSCR_RATES: seedId('case-alicia-dscr', 1),
  ALICIA_LEASE_UPDATES: seedId('case-alicia-lease', 2),
  DAVID_ACH_FAILED: seedId('case-david-ach', 1),
  DAVID_ADD_USER: seedId('case-david-adduser', 2),

  // OpsMatrix Capital - Established Tier
  MICHAEL_CONTACT_UPDATE: seedId('case-michael-contact', 1),
  MICHAEL_REFINANCE: seedId('case-michael-refi', 2),
  JESSICA_INSPECTION: seedId('case-jessica-inspection', 1),
  JESSICA_DRAW_QUESTION: seedId('case-jessica-draw', 2),
  AMANDA_PAYMENT_EXT: seedId('case-amanda-payment', 1),

  // OpsMatrix Capital - Standard Tier
  JENNIFER_FIRST_TIME: seedId('case-jennifer-firsttime', 1),
  JENNIFER_DOC_CHECKLIST: seedId('case-jennifer-docs', 2),
  KEVIN_LOGIN_ISSUES: seedId('case-kevin-login', 1),
  KEVIN_PREQUALIFICATION: seedId('case-kevin-prequal', 2),

  // One Sharpe Capital
  MARIA_LLC_DOCS: seedId('case-maria-llc', 1),
  MARIA_PAYOFF_QUOTE: seedId('case-maria-payoff', 2),
  MARIA_CONSTRUCTION: seedId('case-maria-construction', 3),
  CHRISTOPHER_PHOTOS: seedId('case-christopher-photos', 1),
  CHRISTOPHER_DRAW: seedId('case-christopher-draw', 2),
  CHRISTOPHER_EMAIL: seedId('case-christopher-email', 3),
  STEPHANIE_BEGINNER: seedId('case-stephanie-beginner', 1),
  STEPHANIE_SETUP: seedId('case-stephanie-setup', 2),

  // Issue #469: SLA Breached/Overdue Cases
  OVERDUE_PAYMENT_URGENT: seedId('case-overdue-payment', 1),
  OVERDUE_DOCUMENT_OLD: seedId('case-overdue-doc', 2),
  OVERDUE_SUPPORT_STALE: seedId('case-overdue-support', 3),
} as const;

export type CaseId = (typeof CASE_IDS)[keyof typeof CASE_IDS];

// =============================================================================
// CASE MESSAGE IDs
// =============================================================================

export const CASE_MESSAGE_IDS = {
  // Sample messages for cases
  MARCUS_INSURANCE_MESSAGE_1: seedId('message-marcus-ins', 1),
  MARCUS_INSURANCE_MESSAGE_2: seedId('message-marcus-ins', 2),
  ROBERT_TAX_MESSAGE_1: seedId('message-robert-tax', 1),
  DAVID_ACH_MESSAGE_1: seedId('message-david-ach', 1),
  DAVID_ACH_MESSAGE_2: seedId('message-david-ach', 2),
  MARIA_PAYOFF_MESSAGE_1: seedId('message-maria-payoff', 1),
} as const;

export type CaseMessageId = (typeof CASE_MESSAGE_IDS)[keyof typeof CASE_MESSAGE_IDS];

// =============================================================================
// CASE TYPE CODES
// =============================================================================

/**
 * Case type codes used for matching against API case types.
 * These are semantic codes that will be matched to actual case type UUIDs at runtime.
 */
export const CASE_TYPE_CODES = {
  DOCUMENT: 'document',
  INQUIRY: 'inquiry',
  PAYMENT: 'payment',
  DRAW: 'draw',
  PAYOFF: 'payoff',
  SUPPORT: 'support',
} as const;

export type CaseTypeCode = (typeof CASE_TYPE_CODES)[keyof typeof CASE_TYPE_CODES];

// =============================================================================
// CASE TYPE IDs (for seeding actual case types)
// Names align with canonical defaults in
// 29-case-management-service/src/modules/cases/business-defaults.constants.ts
// so the API seeder, the Prisma seed, and the per-business bootstrap all
// converge on the same 6-row taxonomy.
// =============================================================================

export const CASE_TYPE_IDS = {
  DOCUMENT_REQUEST: seedId('case-type-doc', 1),
  LOAN_INQUIRY: seedId('case-type-inquiry', 2),
  PAYMENT_ISSUE: seedId('case-type-payment', 3),
  DRAW_REQUEST: seedId('case-type-draw', 4),
  PAYOFF_QUOTE: seedId('case-type-payoff', 5),
  GENERAL_SUPPORT: seedId('case-type-support', 6),
} as const;

export type CaseTypeId = (typeof CASE_TYPE_IDS)[keyof typeof CASE_TYPE_IDS];

// =============================================================================
// CASE STATUS IDs (for seeding actual case statuses)
// =============================================================================

// Names align with canonical defaults in
// 29-case-management-service/src/modules/cases/business-defaults.constants.ts
// (12 statuses) so the API seeder, the Prisma seed, and per-business
// bootstrap converge on the same set.
export const CASE_STATUS_IDS = {
  NEW: seedId('case-status-new', 1),
  IN_PROGRESS: seedId('case-status-inprog', 2),
  PENDING_INFO: seedId('case-status-pending-info', 3),
  RESOLVED: seedId('case-status-resolved', 4),
  CLOSED: seedId('case-status-closed', 5),
  PENDING_REVIEW: seedId('case-status-pending-review', 6),
  UNDER_REVIEW: seedId('case-status-under-review', 7),
  PENDING_APPROVAL: seedId('case-status-pending-approval', 8),
  APPROVED: seedId('case-status-approved', 9),
  REJECTED: seedId('case-status-rejected', 10),
  CANCELLED: seedId('case-status-cancelled', 11),
  ARCHIVED: seedId('case-status-archived', 12),
  CONDITIONALLY_APPROVED: seedId('case-status-conditionally-approved', 13),
} as const;

export type CaseStatusId = (typeof CASE_STATUS_IDS)[keyof typeof CASE_STATUS_IDS];

// =============================================================================
// CASE TYPE SEED DATA
// =============================================================================

export interface CaseTypeSeedData {
  id: string;
  businessId: string;
  code: CaseTypeCode;
  name: string;
  description: string;
  icon: string;
  color: string;
  defaultPriority: 'low' | 'medium' | 'high' | 'urgent';
  slaHours: number;
  isActive: boolean;
  sortOrder: number;
}

export const CASE_TYPES: CaseTypeSeedData[] = [
  {
    id: CASE_TYPE_IDS.DOCUMENT_REQUEST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    code: CASE_TYPE_CODES.DOCUMENT,
    name: 'Document Request',
    description: 'Request for additional documentation from borrower',
    icon: 'file-text',
    color: SEED_COLORS.blue,
    defaultPriority: 'medium',
    slaHours: 48,
    isActive: true,
    sortOrder: 1,
  },
  {
    id: CASE_TYPE_IDS.LOAN_INQUIRY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    code: CASE_TYPE_CODES.INQUIRY,
    name: 'Loan Inquiry',
    description: 'Loan status or terms inquiry',
    icon: 'help-circle',
    color: SEED_COLORS.purple,
    defaultPriority: 'medium',
    slaHours: 24,
    isActive: true,
    sortOrder: 2,
  },
  {
    id: CASE_TYPE_IDS.PAYMENT_ISSUE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    code: CASE_TYPE_CODES.PAYMENT,
    name: 'Payment Issue',
    description: 'Payment processing issue',
    icon: 'credit-card',
    color: SEED_COLORS.red,
    defaultPriority: 'high',
    slaHours: 4,
    isActive: true,
    sortOrder: 3,
  },
  {
    id: CASE_TYPE_IDS.DRAW_REQUEST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    code: CASE_TYPE_CODES.DRAW,
    name: 'Draw Request',
    description: 'Construction draw request for active loans',
    icon: 'dollar-sign',
    color: SEED_COLORS.green,
    defaultPriority: 'high',
    slaHours: 72,
    isActive: true,
    sortOrder: 4,
  },
  {
    id: CASE_TYPE_IDS.PAYOFF_QUOTE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    code: CASE_TYPE_CODES.PAYOFF,
    name: 'Payoff Quote',
    description: 'Request for loan payoff quote',
    icon: 'calculator',
    color: SEED_COLORS.amber,
    defaultPriority: 'high',
    slaHours: 24,
    isActive: true,
    sortOrder: 5,
  },
  {
    id: CASE_TYPE_IDS.GENERAL_SUPPORT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    code: CASE_TYPE_CODES.SUPPORT,
    name: 'General Support',
    description: 'General support request',
    icon: 'headphones',
    color: SEED_COLORS.gray,
    defaultPriority: 'low',
    slaHours: 72,
    isActive: true,
    sortOrder: 6,
  },
];

// =============================================================================
// CASE STATUS SEED DATA
// =============================================================================

export interface CaseStatusSeedData {
  id: string;
  businessId: string;
  name: string;
  description: string;
  statusType: 'initial' | 'in_progress' | 'waiting' | 'resolved' | 'closed';
  icon: string;
  color: string;
  isInitial: boolean;
  isFinal: boolean;
  nextStatuses: string[];
  isActive: boolean;
  sortOrder: number;
}

export const CASE_STATUSES: CaseStatusSeedData[] = [
  {
    id: CASE_STATUS_IDS.NEW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'New',
    description: 'Newly created case awaiting review',
    statusType: 'initial',
    icon: 'inbox',
    color: SEED_COLORS.indigo,
    isInitial: true,
    isFinal: false,
    nextStatuses: [CASE_STATUS_IDS.IN_PROGRESS, CASE_STATUS_IDS.CLOSED, CASE_STATUS_IDS.CANCELLED],
    isActive: true,
    sortOrder: 1,
  },
  {
    id: CASE_STATUS_IDS.IN_PROGRESS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'In Progress',
    description: 'Case is being actively worked on',
    statusType: 'in_progress',
    icon: 'loader',
    color: SEED_COLORS.blue,
    isInitial: false,
    isFinal: false,
    nextStatuses: [
      CASE_STATUS_IDS.PENDING_INFO,
      CASE_STATUS_IDS.RESOLVED,
      CASE_STATUS_IDS.CLOSED,
      CASE_STATUS_IDS.CANCELLED,
    ],
    isActive: true,
    sortOrder: 2,
  },
  {
    id: CASE_STATUS_IDS.PENDING_INFO,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Pending Info',
    description: 'Waiting for customer information or external input',
    statusType: 'waiting',
    icon: 'clock',
    color: SEED_COLORS.amber,
    isInitial: false,
    isFinal: false,
    nextStatuses: [
      CASE_STATUS_IDS.IN_PROGRESS,
      CASE_STATUS_IDS.RESOLVED,
      CASE_STATUS_IDS.CLOSED,
      CASE_STATUS_IDS.CANCELLED,
    ],
    isActive: true,
    sortOrder: 3,
  },
  {
    id: CASE_STATUS_IDS.RESOLVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Resolved',
    description: 'Case has been resolved',
    statusType: 'resolved',
    icon: 'check-circle',
    color: SEED_COLORS.green,
    isInitial: false,
    isFinal: false,
    nextStatuses: [CASE_STATUS_IDS.CLOSED, CASE_STATUS_IDS.IN_PROGRESS, CASE_STATUS_IDS.ARCHIVED],
    isActive: true,
    sortOrder: 4,
  },
  {
    id: CASE_STATUS_IDS.CLOSED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Closed',
    description: 'Case is closed and no further action required',
    statusType: 'closed',
    icon: 'archive',
    color: SEED_COLORS.gray,
    isInitial: false,
    isFinal: true,
    nextStatuses: [CASE_STATUS_IDS.ARCHIVED],
    isActive: true,
    sortOrder: 5,
  },
  {
    id: CASE_STATUS_IDS.CANCELLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Cancelled',
    description: 'Case has been cancelled and will not proceed',
    statusType: 'closed',
    icon: 'x-circle',
    color: SEED_COLORS.red,
    isInitial: false,
    isFinal: true,
    nextStatuses: [CASE_STATUS_IDS.ARCHIVED],
    isActive: true,
    sortOrder: 6,
  },
  {
    id: CASE_STATUS_IDS.ARCHIVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Archived',
    description: 'Case has been archived for record-keeping',
    statusType: 'closed',
    icon: 'archive',
    color: SEED_COLORS.grayLight,
    isInitial: false,
    isFinal: true,
    nextStatuses: [],
    isActive: true,
    sortOrder: 7,
  },
];

/**
 * Get all case types for seeding
 */
export function getAllCaseTypes(): CaseTypeSeedData[] {
  return CASE_TYPES;
}

/**
 * Get all case statuses for seeding
 */
export function getAllCaseStatuses(): CaseStatusSeedData[] {
  return CASE_STATUSES;
}

// =============================================================================
// CASE STATUS NAMES
// =============================================================================

/**
 * Case status names used for matching against API case statuses.
 * These are semantic names that will be matched to actual status UUIDs at runtime.
 */
export const CASE_STATUS_NAMES = {
  NEW: 'new',
  IN_PROGRESS: 'in_progress',
  PENDING: 'pending',
  RESOLVED: 'resolved',
} as const;

export type CaseStatusName = (typeof CASE_STATUS_NAMES)[keyof typeof CASE_STATUS_NAMES];

// =============================================================================
// CASE DATA TYPES
// =============================================================================

/**
 * Co-borrower info that can be attached to a case (Issue #469)
 * Stored in case metadata for lending-related cases
 */
export interface CaseCoBorrowerInfo {
  /** Consumer ID of the co-borrower */
  consumerId: string;
  /** Co-borrower's email */
  email: string;
  /** Co-borrower's full name */
  name: string;
  /** Relationship to primary borrower */
  relationship: 'spouse' | 'business_partner' | 'guarantor' | 'other';
}

export interface CaseSeedData {
  id: string;
  businessId: string;
  consumerId: string;
  caseTypeCode: CaseTypeCode;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  statusName: CaseStatusName;
  channel: 'portal' | 'email' | 'phone' | 'chat';
  /** Optional co-borrower info for lending-related cases (Issue #469) */
  coBorrower?: CaseCoBorrowerInfo;
  /** SLA deadline for the case (Issue #469) */
  slaDueAt?: string;
  /** Whether the case SLA has been breached (Issue #469) */
  slaBreached?: boolean;
  /** Case creation timestamp (Issue #469) */
  createdAt?: string;
  /** Assigned user ID (Issue #469) */
  assignedTo?: string;
}

export interface CaseMessageSeedData {
  id: string;
  businessId: string;
  caseId: string;
  content: string;
  messageType: 'note' | 'resolution' | 'response';
  authorId: string;
  authorEmail?: string;
  authorName?: string;
  authorType: 'user' | 'consumer' | 'system';
  isInternal: boolean;
  isPublic: boolean;
}

// =============================================================================
// CASE ATTACHMENT IDs
// =============================================================================

export const CASE_ATTACHMENT_IDS = {
  MARCUS_INSURANCE_DOC: seedId('attachment-marcus-ins', 1),
  ROBERT_TAX_2024: seedId('attachment-robert-tax', 1),
  JESSICA_INSPECTION_REPORT: seedId('attachment-jessica-inspect', 1),
  MARIA_LLC_AGREEMENT: seedId('attachment-maria-llc', 1),
  CHRISTOPHER_PHOTOS_ZIP: seedId('attachment-chris-photos', 1),
} as const;

export type CaseAttachmentId = (typeof CASE_ATTACHMENT_IDS)[keyof typeof CASE_ATTACHMENT_IDS];

// =============================================================================
// CASE ACTIVITY IDs
// =============================================================================

export const CASE_ACTIVITY_IDS = {
  MARCUS_CASE_CREATED: seedId('activity-marcus-created', 1),
  MARCUS_CASE_ASSIGNED: seedId('activity-marcus-assigned', 2),
  MARCUS_STATUS_CHANGED: seedId('activity-marcus-status', 3),
  DAVID_CASE_CREATED: seedId('activity-david-created', 1),
  DAVID_CASE_ESCALATED: seedId('activity-david-escalated', 2),
  MARIA_CASE_CREATED: seedId('activity-maria-created', 1),
} as const;

export type CaseActivityId = (typeof CASE_ACTIVITY_IDS)[keyof typeof CASE_ACTIVITY_IDS];

export interface CaseAttachmentSeedData {
  id: string;
  businessId: string;
  caseId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  storageUrl: string;
  storagePath?: string;
  uploadedBy: string;
  uploadedByEmail?: string;
  uploadedByName?: string;
  description?: string;
  isPublic: boolean;
}

export interface CaseActivitySeedData {
  id: string;
  businessId: string;
  caseId: string;
  activityType:
    | 'created'
    | 'updated'
    | 'assigned'
    | 'status_changed'
    | 'resolved'
    | 'closed'
    | 'reopened'
    | 'message_added'
    | 'attachment_added';
  description: string;
  actorId?: string;
  actorEmail?: string;
  actorName?: string;
  actorType: 'user' | 'consumer' | 'system';
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
}

// =============================================================================
// CASE DATA
// =============================================================================

export const CASES: Record<keyof typeof CASE_IDS, CaseSeedData> = {
  // ==========================================================================
  // OPSMATRIX CAPITAL CASES (Primary demo business)
  // ==========================================================================

  // --- VIP Tier Consumers (Priority handling) ---
  // Marcus Thompson - VIP, 12 completed deals
  MARCUS_INSURANCE_CERT: {
    id: CASE_IDS.MARCUS_INSURANCE_CERT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    caseTypeCode: CASE_TYPE_CODES.DOCUMENT,
    title: 'Missing Insurance Certificate',
    description:
      'Need updated insurance certificate for property at 1234 Oak Valley Dr. Previous certificate expired Jan 15.',
    priority: 'high',
    statusName: CASE_STATUS_NAMES.IN_PROGRESS,
    channel: 'portal',
  },
  MARCUS_PORTFOLIO_EXPANSION: {
    id: CASE_IDS.MARCUS_PORTFOLIO_EXPANSION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    caseTypeCode: CASE_TYPE_CODES.INQUIRY,
    title: 'Portfolio Expansion - 5 Property Package',
    description:
      'Looking to acquire 5 rental properties in Austin market. Need pre-approval for $2.5M package deal. Properties under contract with 30-day close.',
    priority: 'high',
    statusName: CASE_STATUS_NAMES.IN_PROGRESS,
    channel: 'phone',
    // Co-borrower: Sarah Thompson (spouse) is co-signer on portfolio loans
    coBorrower: {
      consumerId: 'co-borrower-sarah-thompson',
      email: 'sarah.thompson@investorpro.com',
      name: 'Sarah Thompson',
      relationship: 'spouse',
    },
  },

  // Robert Chen - VIP, 15 completed deals
  ROBERT_TAX_UPLOAD: {
    id: CASE_IDS.ROBERT_TAX_UPLOAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ROBERT_CHEN,
    caseTypeCode: CASE_TYPE_CODES.DOCUMENT,
    title: 'Tax Return Upload Issue',
    description:
      'Unable to upload 2024 tax returns - file size error. Files are 45MB total (corporate + personal returns).',
    priority: 'medium',
    statusName: CASE_STATUS_NAMES.NEW,
    channel: 'email',
  },
  ROBERT_WIRE_CONFIRM: {
    id: CASE_IDS.ROBERT_WIRE_CONFIRM,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ROBERT_CHEN,
    caseTypeCode: CASE_TYPE_CODES.PAYMENT,
    title: 'Wire Transfer Confirmation',
    description: 'Sent wire for payoff on loan #LC-2024-0892. Wire ref: FED20240115-7823. Please confirm receipt.',
    priority: 'medium',
    statusName: CASE_STATUS_NAMES.RESOLVED,
    channel: 'email',
  },
  ROBERT_PAYOFF_REQUEST: {
    id: CASE_IDS.ROBERT_PAYOFF_REQUEST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ROBERT_CHEN,
    caseTypeCode: CASE_TYPE_CODES.PAYMENT,
    title: 'Payoff Request - Loan #LC-2024-0892',
    description: 'Requesting payoff statement for loan #LC-2024-0892. Planning to sell property and need final amount.',
    priority: 'high',
    statusName: CASE_STATUS_NAMES.IN_PROGRESS,
    channel: 'portal',
    // Co-borrower: Lisa Chen (business partner) is on the original loan
    coBorrower: {
      consumerId: 'co-borrower-lisa-chen',
      email: 'lisa.chen@chenfamilyinvest.com',
      name: 'Lisa Chen',
      relationship: 'business_partner',
    },
  },

  // --- Trusted Tier Consumers ---
  // Alicia Fernandez - Trusted, 7 completed deals
  ALICIA_DSCR_RATES: {
    id: CASE_IDS.ALICIA_DSCR_RATES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ALICIA_FERNANDEZ,
    caseTypeCode: CASE_TYPE_CODES.INQUIRY,
    title: 'DSCR Loan Rates - Tampa Market',
    description:
      'Requesting current rates for DSCR loan on 4-plex in Tampa. Property cash flows at $8,200/month gross. Looking for 75% LTV.',
    priority: 'medium',
    statusName: CASE_STATUS_NAMES.NEW,
    channel: 'phone',
  },
  ALICIA_LEASE_UPDATES: {
    id: CASE_IDS.ALICIA_LEASE_UPDATES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ALICIA_FERNANDEZ,
    caseTypeCode: CASE_TYPE_CODES.DOCUMENT,
    title: 'Lease Agreement Updates',
    description:
      'Uploading renewed lease agreements for properties in Sunbelt portfolio. 3 units renewed at 5% increase.',
    priority: 'low',
    statusName: CASE_STATUS_NAMES.RESOLVED,
    channel: 'portal',
  },

  // David Williams - Trusted, 5 completed deals
  DAVID_ACH_FAILED: {
    id: CASE_IDS.DAVID_ACH_FAILED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.DAVID_WILLIAMS,
    caseTypeCode: CASE_TYPE_CODES.PAYMENT,
    title: 'ACH Payment Failed - Urgent',
    description:
      'Monthly payment ACH rejected due to account number change. New account details attached. Please retry payment to avoid late fee.',
    priority: 'urgent',
    statusName: CASE_STATUS_NAMES.IN_PROGRESS,
    channel: 'phone',
  },
  DAVID_ADD_USER: {
    id: CASE_IDS.DAVID_ADD_USER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.DAVID_WILLIAMS,
    caseTypeCode: CASE_TYPE_CODES.SUPPORT,
    title: 'Add Authorized User to Account',
    description:
      'Please add my assistant Sarah Johnson (sarah@williamsrealty.com) as authorized user for document submissions.',
    priority: 'low',
    statusName: CASE_STATUS_NAMES.RESOLVED,
    channel: 'email',
  },

  // --- Established Tier Consumers ---
  // Michael Brown - Established, 3 completed deals
  MICHAEL_CONTACT_UPDATE: {
    id: CASE_IDS.MICHAEL_CONTACT_UPDATE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MICHAEL_BROWN,
    caseTypeCode: CASE_TYPE_CODES.SUPPORT,
    title: 'Update Contact Information',
    description:
      'Need to update phone number to 832-555-9999 and mailing address to 4521 Westheimer Rd, Houston TX 77027.',
    priority: 'low',
    statusName: CASE_STATUS_NAMES.RESOLVED,
    channel: 'email',
  },
  MICHAEL_REFINANCE: {
    id: CASE_IDS.MICHAEL_REFINANCE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MICHAEL_BROWN,
    caseTypeCode: CASE_TYPE_CODES.INQUIRY,
    title: 'Refinance Options for Rental Property',
    description:
      'Current loan at 8.5% - interested in refinancing if rates have improved. Property value increased 15% since purchase.',
    priority: 'medium',
    statusName: CASE_STATUS_NAMES.IN_PROGRESS,
    channel: 'portal',
  },

  // Jessica Lee - Established, 2 completed deals
  JESSICA_INSPECTION: {
    id: CASE_IDS.JESSICA_INSPECTION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.JESSICA_LEE,
    caseTypeCode: CASE_TYPE_CODES.DOCUMENT,
    title: 'Property Inspection Report Required',
    description:
      'Loan processor requested updated inspection report for 2847 Maple St property. Scheduling inspector this week.',
    priority: 'medium',
    statusName: CASE_STATUS_NAMES.IN_PROGRESS,
    channel: 'portal',
    // Co-borrower: James Lee (guarantor) is on the fix-and-flip loan
    coBorrower: {
      consumerId: 'co-borrower-james-lee',
      email: 'james.lee@leeinvestments.com',
      name: 'James Lee',
      relationship: 'guarantor',
    },
  },
  JESSICA_DRAW_QUESTION: {
    id: CASE_IDS.JESSICA_DRAW_QUESTION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.JESSICA_LEE,
    caseTypeCode: CASE_TYPE_CODES.INQUIRY,
    title: 'Draw Request Process Question',
    description:
      'First fix-and-flip loan. How do I submit draw requests for renovation funds? What documentation is needed?',
    priority: 'low',
    statusName: CASE_STATUS_NAMES.RESOLVED,
    channel: 'phone',
  },

  // Amanda Wilson - Established, 2 completed deals
  AMANDA_PAYMENT_EXT: {
    id: CASE_IDS.AMANDA_PAYMENT_EXT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.AMANDA_WILSON,
    caseTypeCode: CASE_TYPE_CODES.PAYMENT,
    title: 'Payment Extension Request',
    description:
      'Requesting 15-day extension on March payment due to closing delay on sale property. Sale closes March 20th.',
    priority: 'high',
    statusName: CASE_STATUS_NAMES.PENDING,
    channel: 'email',
  },

  // --- Standard Tier Consumers (New borrowers) ---
  // Jennifer Martinez - Standard, 0 completed deals
  JENNIFER_FIRST_TIME: {
    id: CASE_IDS.JENNIFER_FIRST_TIME,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    caseTypeCode: CASE_TYPE_CODES.INQUIRY,
    title: 'First-Time Borrower Questions',
    description:
      'Questions about fix & flip loan process and requirements. Found property in Austin for $280K, needs $50K rehab.',
    priority: 'low',
    statusName: CASE_STATUS_NAMES.RESOLVED,
    channel: 'portal',
  },
  JENNIFER_DOC_CHECKLIST: {
    id: CASE_IDS.JENNIFER_DOC_CHECKLIST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    caseTypeCode: CASE_TYPE_CODES.DOCUMENT,
    title: 'Application Document Checklist',
    description: 'Need clarification on required documents for loan application. Is 2 years tax returns sufficient?',
    priority: 'low',
    statusName: CASE_STATUS_NAMES.NEW,
    channel: 'email',
  },

  // Kevin Johnson - Standard, 0 completed deals
  KEVIN_LOGIN_ISSUES: {
    id: CASE_IDS.KEVIN_LOGIN_ISSUES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.KEVIN_JOHNSON,
    caseTypeCode: CASE_TYPE_CODES.SUPPORT,
    title: 'Portal Login Issues',
    description:
      'Password reset not working - not receiving reset email. Checked spam folder. Email: kevin.johnson@outlook.com',
    priority: 'medium',
    statusName: CASE_STATUS_NAMES.RESOLVED,
    channel: 'phone',
  },
  KEVIN_PREQUALIFICATION: {
    id: CASE_IDS.KEVIN_PREQUALIFICATION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.KEVIN_JOHNSON,
    caseTypeCode: CASE_TYPE_CODES.INQUIRY,
    title: 'Pre-Qualification Request',
    description:
      'Want to get pre-qualified before making offers. Annual income $185K, credit score 720. Looking at properties $200-300K.',
    priority: 'medium',
    statusName: CASE_STATUS_NAMES.IN_PROGRESS,
    channel: 'portal',
    // SLA tracking (Issue #469)
    createdAt: '2026-01-28T10:00:00Z',
    slaDueAt: '2026-01-29T10:00:00Z', // 24-hour SLA for inquiries
    slaBreached: false,
    assignedTo: USER_IDS.SARAH_CHEN,
  },

  // ==========================================================================
  // ONE SHARPE CAPITAL CASES (Secondary demo business)
  // ==========================================================================

  // Maria Santos - Trusted, 6 completed deals (One Sharpe customer)
  MARIA_LLC_DOCS: {
    id: CASE_IDS.MARIA_LLC_DOCS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.MARIA_SANTOS,
    caseTypeCode: CASE_TYPE_CODES.DOCUMENT,
    title: 'Entity Documents for New LLC',
    description:
      'Formed new LLC (Santos Ventures II LLC) for next acquisition. Uploading operating agreement and EIN letter.',
    priority: 'medium',
    statusName: CASE_STATUS_NAMES.NEW,
    channel: 'portal',
  },
  MARIA_PAYOFF_QUOTE: {
    id: CASE_IDS.MARIA_PAYOFF_QUOTE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.MARIA_SANTOS,
    caseTypeCode: CASE_TYPE_CODES.PAYMENT,
    title: 'Payoff Quote Request',
    description: 'Requesting payoff quote for loan #1SC-2023-0456. Planning to sell property, close date Feb 28.',
    priority: 'high',
    statusName: CASE_STATUS_NAMES.IN_PROGRESS,
    channel: 'email',
  },
  MARIA_CONSTRUCTION: {
    id: CASE_IDS.MARIA_CONSTRUCTION,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.MARIA_SANTOS,
    caseTypeCode: CASE_TYPE_CODES.INQUIRY,
    title: 'Construction Loan Options',
    description: 'Interested in ground-up construction loan for vacant lot in San Antonio. 8-unit multifamily project.',
    priority: 'medium',
    statusName: CASE_STATUS_NAMES.NEW,
    channel: 'phone',
    // Co-borrower: Carlos Santos (business partner) for the multifamily project
    coBorrower: {
      consumerId: 'co-borrower-carlos-santos',
      email: 'carlos.santos@santosventures.com',
      name: 'Carlos Santos',
      relationship: 'business_partner',
    },
  },

  // Christopher Davis - Established, 4 completed deals (One Sharpe customer)
  CHRISTOPHER_PHOTOS: {
    id: CASE_IDS.CHRISTOPHER_PHOTOS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    caseTypeCode: CASE_TYPE_CODES.DOCUMENT,
    title: 'Renovation Completion Photos',
    description:
      'Uploading final renovation photos for 1892 Cedar Lane flip. Ready for final inspection and sale listing.',
    priority: 'medium',
    statusName: CASE_STATUS_NAMES.IN_PROGRESS,
    channel: 'portal',
  },
  CHRISTOPHER_DRAW: {
    id: CASE_IDS.CHRISTOPHER_DRAW,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    caseTypeCode: CASE_TYPE_CODES.PAYMENT,
    title: 'Draw Request #3 - Final Draw',
    description:
      'Submitting final draw request for $18,500. Kitchen and bathroom renovations complete. Receipts attached.',
    priority: 'high',
    statusName: CASE_STATUS_NAMES.PENDING,
    channel: 'portal',
  },
  CHRISTOPHER_EMAIL: {
    id: CASE_IDS.CHRISTOPHER_EMAIL,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    caseTypeCode: CASE_TYPE_CODES.SUPPORT,
    title: 'Change Primary Email Address',
    description:
      'Please update my primary email from chris.davis@davisflipping.com to chris@davisflipping.com (new domain).',
    priority: 'low',
    statusName: CASE_STATUS_NAMES.RESOLVED,
    channel: 'email',
  },

  // Stephanie Garcia - Standard, 0 completed deals (One Sharpe customer)
  STEPHANIE_BEGINNER: {
    id: CASE_IDS.STEPHANIE_BEGINNER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.STEPHANIE_GARCIA,
    caseTypeCode: CASE_TYPE_CODES.INQUIRY,
    title: 'Beginner Investor Questions',
    description:
      'New to real estate investing. Looking for guidance on BRRRR strategy loans. Budget around $150K for first property.',
    priority: 'low',
    statusName: CASE_STATUS_NAMES.NEW,
    channel: 'portal',
  },
  STEPHANIE_SETUP: {
    id: CASE_IDS.STEPHANIE_SETUP,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.STEPHANIE_GARCIA,
    caseTypeCode: CASE_TYPE_CODES.SUPPORT,
    title: 'Account Setup Assistance',
    description: 'Having trouble completing my investor profile. The form keeps timing out on the upload section.',
    priority: 'medium',
    statusName: CASE_STATUS_NAMES.IN_PROGRESS,
    channel: 'phone',
  },

  // ==========================================================================
  // ISSUE #469: SLA BREACHED/OVERDUE CASES
  // ==========================================================================

  // Urgent payment case - 4-hour SLA breached by 12+ hours
  OVERDUE_PAYMENT_URGENT: {
    id: CASE_IDS.OVERDUE_PAYMENT_URGENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.DAVID_WILLIAMS,
    caseTypeCode: CASE_TYPE_CODES.PAYMENT,
    title: 'Wire Transfer Not Received - URGENT',
    description:
      'Sent wire for $1.2M closing yesterday at 2pm. No confirmation yet. Title company needs funds today or deal falls through.',
    priority: 'urgent',
    statusName: CASE_STATUS_NAMES.IN_PROGRESS,
    channel: 'phone',
    createdAt: '2026-01-29T14:00:00Z',
    slaDueAt: '2026-01-29T18:00:00Z', // 4-hour SLA for payments
    slaBreached: true, // 12+ hours overdue
    assignedTo: USER_IDS.JAMES_WILSON,
  },

  // Document case - 48-hour SLA breached by 3 days
  OVERDUE_DOCUMENT_OLD: {
    id: CASE_IDS.OVERDUE_DOCUMENT_OLD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MICHAEL_BROWN,
    caseTypeCode: CASE_TYPE_CODES.DOCUMENT,
    title: 'Appraisal Report Missing',
    description:
      'Submitted appraisal report last week but shows as missing in system. Uploaded twice. Need confirmation of receipt.',
    priority: 'high',
    statusName: CASE_STATUS_NAMES.PENDING,
    channel: 'email',
    createdAt: '2026-01-24T09:00:00Z',
    slaDueAt: '2026-01-26T09:00:00Z', // 48-hour SLA for documents
    slaBreached: true, // 3+ days overdue
    assignedTo: USER_IDS.MIKE_CHEN,
  },

  // Support case - 72-hour SLA breached by 5 days
  OVERDUE_SUPPORT_STALE: {
    id: CASE_IDS.OVERDUE_SUPPORT_STALE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.STEPHANIE_GARCIA,
    caseTypeCode: CASE_TYPE_CODES.SUPPORT,
    title: 'Cannot Access Historical Statements',
    description: 'Unable to view statements older than 6 months in the portal. Need access for tax filing deadline.',
    priority: 'medium',
    statusName: CASE_STATUS_NAMES.NEW,
    channel: 'portal',
    createdAt: '2026-01-22T11:00:00Z',
    slaDueAt: '2026-01-25T11:00:00Z', // 72-hour SLA for support
    slaBreached: true, // 5+ days overdue, never assigned
  },
};

// =============================================================================
// CASE MESSAGE DATA
// =============================================================================

export const CASE_MESSAGES: Record<keyof typeof CASE_MESSAGE_IDS, CaseMessageSeedData> = {
  MARCUS_INSURANCE_MESSAGE_1: {
    id: CASE_MESSAGE_IDS.MARCUS_INSURANCE_MESSAGE_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    content:
      'Hi Marcus, I see your insurance certificate expired. Please contact your insurance provider for an updated certificate showing coverage through at least the loan maturity date.',
    messageType: 'response',
    authorId: USER_IDS.RACHEL_MARTINEZ,
    authorEmail: 'rachel.martinez@opsmatrix.local',
    authorName: 'Rachel Martinez',
    authorType: 'user',
    isInternal: false,
    isPublic: true,
  },
  MARCUS_INSURANCE_MESSAGE_2: {
    id: CASE_MESSAGE_IDS.MARCUS_INSURANCE_MESSAGE_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    content: 'VIP client - prioritize this request. Marcus has 12 completed deals and excellent payment history.',
    messageType: 'note',
    authorId: USER_IDS.SARAH_CHEN,
    authorEmail: 'sarah.chen@opsmatrix.local',
    authorName: 'Sarah Chen',
    authorType: 'user',
    isInternal: true,
    isPublic: false,
  },
  ROBERT_TAX_MESSAGE_1: {
    id: CASE_MESSAGE_IDS.ROBERT_TAX_MESSAGE_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.ROBERT_TAX_UPLOAD,
    content:
      "Mr. Chen, we've increased your upload limit to 100MB. Please try uploading again. If issues persist, you can email documents directly to docs@opsmatrix.com.",
    messageType: 'response',
    authorId: USER_IDS.MIKE_JOHNSON,
    authorEmail: 'mike.johnson@opsmatrix.local',
    authorName: 'Mike Johnson',
    authorType: 'user',
    isInternal: false,
    isPublic: true,
  },
  DAVID_ACH_MESSAGE_1: {
    id: CASE_MESSAGE_IDS.DAVID_ACH_MESSAGE_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.DAVID_ACH_FAILED,
    content: 'Urgent - ACH rejection. Need to update banking info before next payment cycle to avoid late fee.',
    messageType: 'note',
    authorId: USER_IDS.SUPER_ADMIN,
    authorType: 'system',
    isInternal: true,
    isPublic: false,
  },
  DAVID_ACH_MESSAGE_2: {
    id: CASE_MESSAGE_IDS.DAVID_ACH_MESSAGE_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.DAVID_ACH_FAILED,
    content:
      "David, we've received your new banking details. Processing the update now. Your payment will be retried within 24 hours.",
    messageType: 'response',
    authorId: USER_IDS.JAMES_WILSON,
    authorEmail: 'james.wilson@opsmatrix.local',
    authorName: 'James Wilson',
    authorType: 'user',
    isInternal: false,
    isPublic: true,
  },
  MARIA_PAYOFF_MESSAGE_1: {
    id: CASE_MESSAGE_IDS.MARIA_PAYOFF_MESSAGE_1,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    caseId: CASE_IDS.MARIA_PAYOFF_QUOTE,
    content:
      'Payoff quote generated: $342,567.89 good through Feb 28. Wire instructions attached. Please allow 2 business days for payoff processing.',
    messageType: 'resolution',
    authorId: USER_IDS.SHARPE_ADMIN,
    authorEmail: 'admin@1sharpe.local',
    authorName: 'Sharpe Admin',
    authorType: 'user',
    isInternal: false,
    isPublic: true,
  },
};

// =============================================================================
// CASE ATTACHMENT DATA
// =============================================================================

export const CASE_ATTACHMENTS: Record<keyof typeof CASE_ATTACHMENT_IDS, CaseAttachmentSeedData> = {
  MARCUS_INSURANCE_DOC: {
    id: CASE_ATTACHMENT_IDS.MARCUS_INSURANCE_DOC,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    fileName: 'insurance-certificate-1234-oak-valley.pdf',
    fileType: 'application/pdf',
    fileSize: 245760, // 240KB
    storageUrl: 'https://storage.opsmatrix.local/attachments/marcus-insurance-cert.pdf',
    storagePath: '/attachments/cases/marcus-thompson/insurance-certificate-1234-oak-valley.pdf',
    uploadedBy: CONSUMER_IDS.MARCUS_THOMPSON,
    uploadedByEmail: 'marcus.thompson@investorpro.com',
    uploadedByName: 'Marcus Thompson',
    description: 'Updated insurance certificate for 1234 Oak Valley Dr property',
    isPublic: true,
  },
  ROBERT_TAX_2024: {
    id: CASE_ATTACHMENT_IDS.ROBERT_TAX_2024,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.ROBERT_TAX_UPLOAD,
    fileName: 'chen-enterprises-2024-tax-return.pdf',
    fileType: 'application/pdf',
    fileSize: 47185920, // 45MB
    storageUrl: 'https://storage.opsmatrix.local/attachments/robert-tax-2024.pdf',
    storagePath: '/attachments/cases/robert-chen/chen-enterprises-2024-tax-return.pdf',
    uploadedBy: CONSUMER_IDS.ROBERT_CHEN,
    uploadedByEmail: 'robert.chen@chenenterprises.com',
    uploadedByName: 'Robert Chen',
    description: '2024 corporate and personal tax returns',
    isPublic: false,
  },
  JESSICA_INSPECTION_REPORT: {
    id: CASE_ATTACHMENT_IDS.JESSICA_INSPECTION_REPORT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.JESSICA_INSPECTION,
    fileName: 'property-inspection-2847-maple.pdf',
    fileType: 'application/pdf',
    fileSize: 5242880, // 5MB
    storageUrl: 'https://storage.opsmatrix.local/attachments/jessica-inspection.pdf',
    storagePath: '/attachments/cases/jessica-lee/property-inspection-2847-maple.pdf',
    uploadedBy: CONSUMER_IDS.JESSICA_LEE,
    uploadedByEmail: 'jessica.lee@texasflippers.com',
    uploadedByName: 'Jessica Lee',
    description: 'Updated property inspection report for 2847 Maple St',
    isPublic: true,
  },
  MARIA_LLC_AGREEMENT: {
    id: CASE_ATTACHMENT_IDS.MARIA_LLC_AGREEMENT,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    caseId: CASE_IDS.MARIA_LLC_DOCS,
    fileName: 'santos-ventures-ii-operating-agreement.pdf',
    fileType: 'application/pdf',
    fileSize: 1048576, // 1MB
    storageUrl: 'https://storage.opsmatrix.local/attachments/maria-llc-agreement.pdf',
    storagePath: '/attachments/cases/maria-santos/santos-ventures-ii-operating-agreement.pdf',
    uploadedBy: CONSUMER_IDS.MARIA_SANTOS,
    uploadedByEmail: 'maria.santos@santosventures.com',
    uploadedByName: 'Maria Santos',
    description: 'Santos Ventures II LLC Operating Agreement and EIN Letter',
    isPublic: false,
  },
  CHRISTOPHER_PHOTOS_ZIP: {
    id: CASE_ATTACHMENT_IDS.CHRISTOPHER_PHOTOS_ZIP,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    caseId: CASE_IDS.CHRISTOPHER_PHOTOS,
    fileName: 'renovation-photos-1892-cedar-lane.zip',
    fileType: 'application/zip',
    fileSize: 52428800, // 50MB
    storageUrl: 'https://storage.opsmatrix.local/attachments/chris-photos.zip',
    storagePath: '/attachments/cases/christopher-davis/renovation-photos-1892-cedar-lane.zip',
    uploadedBy: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    uploadedByEmail: 'chris.davis@davisflipping.com',
    uploadedByName: 'Christopher Davis',
    description: 'Final renovation completion photos for 1892 Cedar Lane flip project',
    isPublic: true,
  },
};

// =============================================================================
// CASE ACTIVITY DATA
// =============================================================================

export const CASE_ACTIVITIES: Record<keyof typeof CASE_ACTIVITY_IDS, CaseActivitySeedData> = {
  MARCUS_CASE_CREATED: {
    id: CASE_ACTIVITY_IDS.MARCUS_CASE_CREATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    activityType: 'created',
    description: 'Case created via borrower portal',
    actorId: CONSUMER_IDS.MARCUS_THOMPSON,
    actorEmail: 'marcus.thompson@investorpro.com',
    actorName: 'Marcus Thompson',
    actorType: 'consumer',
  },
  MARCUS_CASE_ASSIGNED: {
    id: CASE_ACTIVITY_IDS.MARCUS_CASE_ASSIGNED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    activityType: 'assigned',
    description: 'Case assigned to Rachel Martinez (VIP queue)',
    actorId: USER_IDS.SUPER_ADMIN,
    actorType: 'system',
    fieldName: 'assignedTo',
    oldValue: undefined,
    newValue: USER_IDS.RACHEL_MARTINEZ,
  },
  MARCUS_STATUS_CHANGED: {
    id: CASE_ACTIVITY_IDS.MARCUS_STATUS_CHANGED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    activityType: 'status_changed',
    description: 'Status changed from New to In Progress',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    fieldName: 'status',
    oldValue: 'new',
    newValue: 'in_progress',
  },
  DAVID_CASE_CREATED: {
    id: CASE_ACTIVITY_IDS.DAVID_CASE_CREATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.DAVID_ACH_FAILED,
    activityType: 'created',
    description: 'Urgent case created via phone support',
    actorId: USER_IDS.MIKE_JOHNSON,
    actorEmail: 'mike.johnson@opsmatrix.local',
    actorName: 'Mike Johnson',
    actorType: 'user',
  },
  DAVID_CASE_ESCALATED: {
    id: CASE_ACTIVITY_IDS.DAVID_CASE_ESCALATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.DAVID_ACH_FAILED,
    activityType: 'status_changed',
    description: 'Case escalated to payments team due to urgency',
    actorId: USER_IDS.SUPER_ADMIN,
    actorType: 'system',
    fieldName: 'priority',
    oldValue: 'high',
    newValue: 'urgent',
  },
  MARIA_CASE_CREATED: {
    id: CASE_ACTIVITY_IDS.MARIA_CASE_CREATED,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    caseId: CASE_IDS.MARIA_LLC_DOCS,
    activityType: 'created',
    description: 'Case created via borrower portal',
    actorId: CONSUMER_IDS.MARIA_SANTOS,
    actorEmail: 'maria.santos@santosventures.com',
    actorName: 'Maria Santos',
    actorType: 'consumer',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all cases as an array for seeding
 */
export function getAllCases(): CaseSeedData[] {
  return Object.values(CASES);
}

/**
 * Get cases for a specific business
 */
export function getCasesByBusiness(businessId: string): CaseSeedData[] {
  return Object.values(CASES).filter((c) => c.businessId === businessId);
}

/**
 * Get cases for a specific consumer
 */
export function getCasesByConsumer(consumerId: string): CaseSeedData[] {
  return Object.values(CASES).filter((c) => c.consumerId === consumerId);
}

/**
 * Get all case messages as an array for seeding
 */
export function getAllCaseMessages(): CaseMessageSeedData[] {
  return Object.values(CASE_MESSAGES);
}

/**
 * Get messages for a specific case
 */
export function getMessagesByCase(caseId: string): CaseMessageSeedData[] {
  return Object.values(CASE_MESSAGES).filter((c) => c.caseId === caseId);
}

/**
 * Get cases by status name
 */
export function getCasesByStatus(statusName: CaseStatusName): CaseSeedData[] {
  return Object.values(CASES).filter((c) => c.statusName === statusName);
}

/**
 * Get cases by priority
 */
export function getCasesByPriority(priority: CaseSeedData['priority']): CaseSeedData[] {
  return Object.values(CASES).filter((c) => c.priority === priority);
}

/**
 * Get all case attachments as an array for seeding
 */
export function getAllCaseAttachments(): CaseAttachmentSeedData[] {
  return Object.values(CASE_ATTACHMENTS);
}

/**
 * Get attachments for a specific case
 */
export function getAttachmentsByCase(caseId: string): CaseAttachmentSeedData[] {
  return Object.values(CASE_ATTACHMENTS).filter((a) => a.caseId === caseId);
}

/**
 * Get all case activities as an array for seeding
 */
export function getAllCaseActivities(): CaseActivitySeedData[] {
  return Object.values(CASE_ACTIVITIES);
}

/**
 * Get activities for a specific case
 */
export function getActivitiesByCase(caseId: string): CaseActivitySeedData[] {
  return Object.values(CASE_ACTIVITIES).filter((a) => a.caseId === caseId);
}

/**
 * Get attachments for a specific business
 */
export function getAttachmentsByBusiness(businessId: string): CaseAttachmentSeedData[] {
  return Object.values(CASE_ATTACHMENTS).filter((a) => a.businessId === businessId);
}

/**
 * Get activities for a specific business
 */
export function getActivitiesByBusiness(businessId: string): CaseActivitySeedData[] {
  return Object.values(CASE_ACTIVITIES).filter((a) => a.businessId === businessId);
}

/**
 * Get cases with SLA breaches (Issue #469)
 */
export function getOverdueCases(): CaseSeedData[] {
  return Object.values(CASES).filter((c) => c.slaBreached === true);
}

/**
 * Get cases with SLA deadlines defined (Issue #469)
 */
export function getCasesWithSlaDeadlines(): CaseSeedData[] {
  return Object.values(CASES).filter((c) => c.slaDueAt !== undefined);
}

/**
 * Get cases assigned to a specific user (Issue #469)
 */
export function getCasesByAssignee(userId: string): CaseSeedData[] {
  return Object.values(CASES).filter((c) => c.assignedTo === userId);
}

// =============================================================================
// E2E CASE SEED IDS
// =============================================================================

/**
 * E2E Case Seed IDs for canary test scenarios (Issue #282)
 * These IDs are used by the Control Tower CaseExtendedExecutor
 * @see Issue #282 - Case Management CRUD E2E scenarios
 */
export const E2E_CASE_SEED_IDS = {
  // -------------------------------------------------------------------------
  // Case Types - for E2E type CRUD testing
  // -------------------------------------------------------------------------
  /** Default case type for loan applications — points at canonical Loan Inquiry */
  TYPE_LOAN_APPLICATION: CASE_TYPE_IDS.LOAN_INQUIRY,
  /** Document request case type */
  TYPE_DOCUMENT_REQUEST: CASE_TYPE_IDS.DOCUMENT_REQUEST,
  /** Payment issue case type (4-hour SLA for testing breach scenarios) */
  TYPE_PAYMENT_ISSUE: CASE_TYPE_IDS.PAYMENT_ISSUE,
  /** Support ticket case type — points at canonical General Support */
  TYPE_SUPPORT_TICKET: CASE_TYPE_IDS.GENERAL_SUPPORT,

  // -------------------------------------------------------------------------
  // Case Statuses - for E2E status transition testing
  // -------------------------------------------------------------------------
  /** Initial status for new cases */
  STATUS_NEW: CASE_STATUS_IDS.NEW,
  /** In-progress status */
  STATUS_IN_PROGRESS: CASE_STATUS_IDS.IN_PROGRESS,
  /** Pending/waiting status — canonical name is "Pending Info" */
  STATUS_PENDING: CASE_STATUS_IDS.PENDING_INFO,
  /** Resolved status */
  STATUS_RESOLVED: CASE_STATUS_IDS.RESOLVED,
  /** Closed/final status */
  STATUS_CLOSED: CASE_STATUS_IDS.CLOSED,

  // -------------------------------------------------------------------------
  // Cases - for E2E CRUD and workflow testing
  // -------------------------------------------------------------------------
  /** Case in NEW status for testing status transitions */
  CASE_NEW: CASE_IDS.ROBERT_TAX_UPLOAD,
  /** Case in IN_PROGRESS status for testing updates and comments */
  CASE_IN_PROGRESS: CASE_IDS.MARCUS_INSURANCE_CERT,
  /** Case in PENDING status for testing pending workflows */
  CASE_PENDING: CASE_IDS.AMANDA_PAYMENT_EXT,
  /** Case in RESOLVED status for testing resolved case operations */
  CASE_RESOLVED: CASE_IDS.ROBERT_WIRE_CONFIRM,
  /** Urgent priority case for testing priority filtering */
  CASE_URGENT: CASE_IDS.DAVID_ACH_FAILED,
  /** Case with SLA tracking - uses 4-hour SLA type for breach testing */
  CASE_FOR_SLA: CASE_IDS.DAVID_ACH_FAILED,

  // -------------------------------------------------------------------------
  // Messages - for E2E message CRUD testing
  // -------------------------------------------------------------------------
  /** Internal message (not visible to consumers) */
  MESSAGE_INTERNAL: CASE_MESSAGE_IDS.MARCUS_INSURANCE_MESSAGE_2,
  /** Public message (visible to consumers) */
  MESSAGE_PUBLIC: CASE_MESSAGE_IDS.MARCUS_INSURANCE_MESSAGE_1,
  /** Resolution message */
  MESSAGE_RESOLUTION: CASE_MESSAGE_IDS.MARIA_PAYOFF_MESSAGE_1,

  // -------------------------------------------------------------------------
  // Attachments - for E2E attachment CRUD testing
  // -------------------------------------------------------------------------
  /** PDF attachment for download testing */
  ATTACHMENT_PDF: CASE_ATTACHMENT_IDS.MARCUS_INSURANCE_DOC,
  /** Large file attachment for size limit testing */
  ATTACHMENT_LARGE: CASE_ATTACHMENT_IDS.ROBERT_TAX_2024,
  /** ZIP attachment for multi-file testing */
  ATTACHMENT_ZIP: CASE_ATTACHMENT_IDS.CHRISTOPHER_PHOTOS_ZIP,

  // -------------------------------------------------------------------------
  // Activities - for E2E activity tracking testing
  // -------------------------------------------------------------------------
  /** Case created activity */
  ACTIVITY_CREATED: CASE_ACTIVITY_IDS.MARCUS_CASE_CREATED,
  /** Case assigned activity */
  ACTIVITY_ASSIGNED: CASE_ACTIVITY_IDS.MARCUS_CASE_ASSIGNED,
  /** Status changed activity */
  ACTIVITY_STATUS_CHANGED: CASE_ACTIVITY_IDS.MARCUS_STATUS_CHANGED,
} as const;

export type E2ECaseSeedId = (typeof E2E_CASE_SEED_IDS)[keyof typeof E2E_CASE_SEED_IDS];
